import { getCalApi } from '@calcom/embed-react';
import { removeChildElements } from '@montugroup/frontend-common';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { BookingDetails } from '@/components/common/CalendarPopupButton';
import settings from '@/constants/constants';
import type { PostBookingTrigger } from '@/hooks/analytics/useConsultationWaitlistTracking';
import useGetConsultations from '@/hooks/consultations/useGetConsultations';
import { consultationCardId } from '@/pages/consultations/components/UpcomingConsultationCard';
import type { PostBookingPrompt } from '@/pages/consultations/hooks/usePostBookingWaitlistPrompt';
import { Logger } from '@/utils/logger';

// cal.com can't remove listeners, so register them once per cal api and send each event to the mounted page.
// Registering from an effect would add another set on every remount, and on the theme flip in dark mode.
let onCalBooking: ((trigger: PostBookingTrigger, details?: BookingDetails) => void) | null = null;
const calApisWithListeners = new WeakSet<object>();
const logger = new Logger('useBookingCompletedPrompt');

const registerCalListeners = async () => {
  const cal = await getCalApi(settings.calendarConfig.embedJsUrl);
  if (calApisWithListeners.has(cal)) {
    return;
  }
  calApisWithListeners.add(cal);

  const listen = (action: 'bookingSuccessful' | 'rescheduleBookingSuccessful', trigger: PostBookingTrigger) =>
    cal('on', {
      action,
      callback: ({ detail }) => {
        // the listener outlives the page, so leave bookings made elsewhere in the app to their own handlers
        if (!onCalBooking) {
          return;
        }
        // clean up first, so an error opening the prompt can't leave the popup and the scroll lock on screen
        removeChildElements('cal-modal-box');
        document.body.removeAttribute('style'); // this removes overflow hidden style applied by Calcom

        onCalBooking(trigger, detail.data as BookingDetails);
      },
    });
  listen('bookingSuccessful', 'booking');
  listen('rescheduleBookingSuccessful', 'reschedule');
};

/**
 * Opens the post-booking waitlist prompt after a booking (BookConsultationCard) or reschedule (any
 * UpcomingConsultationCard) while the page is mounted; the dialog only appears if pms-service has put the booked doctor
 * on the waitlist. Use it once, on the consultations page: the listeners send each event to one mounted caller, so a
 * second one would take the events over.
 * @returns the prompt to show, with a key per booking, and a close that moves focus to the booked consultation's card
 */
export function useBookingCompletedPrompt() {
  const { consultData, isSuccess } = useGetConsultations({ when: 'upcoming' });
  const [prompt, setPrompt] = useState<(PostBookingPrompt & { key: number }) | null>(null);
  // read through a ref so the prompt sees the list as it was when the event fired; updated after render, as React
  // Compiler skips a hook that writes a ref while rendering, and events only arrive after the commit
  const upcomingRef = useRef({ consultations: consultData, isLoaded: isSuccess });
  useEffect(() => {
    upcomingRef.current = { consultations: consultData, isLoaded: isSuccess };
  });
  const promptKeyRef = useRef(0);

  const open = useCallback((trigger: PostBookingTrigger, details?: BookingDetails) => {
    promptKeyRef.current += 1;
    setPrompt({
      key: promptKeyRef.current,
      trigger,
      uid: details?.booking?.uid,
      // an empty list that hasn't loaded isn't a baseline: every consultation would look new
      knownUids: upcomingRef.current.isLoaded
        ? upcomingRef.current.consultations.flatMap((consultation) =>
            consultation.calcomBookingUuid ? [consultation.calcomBookingUuid] : [],
          )
        : null,
    });
  }, []);

  const close = useCallback((bookedConsultationId?: number) => {
    setPrompt(null);
    if (bookedConsultationId === undefined) {
      return;
    }
    // focus was in the cal.com popup, which has gone, so move it to the new booking's card; on the next frame, after
    // the dialog has tried to put it back
    requestAnimationFrame(() => document.getElementById(consultationCardId(bookedConsultationId))?.focus());
  }, []);

  useEffect(() => {
    registerCalListeners().catch((error) => {
      // e.g. the embed script was blocked; the page still works, without the post-booking prompt
      logger.error('error registering the cal.com booking listeners', error);
    });
    onCalBooking = open;
    return () => {
      if (onCalBooking === open) {
        onCalBooking = null;
      }
    };
  }, [open]);

  return { prompt, close };
}
